import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "../../constants/testIds";
import { IAWordmark } from "./Wordmarks";

const LINKS = [
  { to: "/", label: "Home", testId: NAV.linkHome, end: true },
  { to: "/about", label: "About", testId: NAV.linkAbout },
  { to: "/indian-alchemy", label: "Indian Alchemy", testId: NAV.linkIA },
  { to: "/platinum-hospitality", label: "Platinum Hospitality", testId: NAV.linkPH },
  { to: "/portfolio", label: "Portfolio", testId: NAV.linkPortfolio },
  { to: "/contact", label: "Contact", testId: NAV.linkContact },
  { to: "/deposit", label: "Client Payments", testId: NAV.linkDeposit },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onDark = pathname === "/" || pathname === "/deposit";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || !onDark;
  const linkClass = ({ isActive }) =>
    `text-[11px] uppercase tracking-[0.24em] transition-colors ${
      isActive
        ? "text-[#C9A961]"
        : solid
        ? "text-[#3B1F1A]/80 hover:text-[#C9A961]"
        : "text-[#FDFBF6]/90 hover:text-[#C9A961]"
    }`;

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        solid
          ? "bg-[#FDFBF6]/92 backdrop-blur-md border-b border-[#3B1F1A]/8"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <NavLink to="/" data-testid={NAV.logoLink} className="flex items-center shrink-0 mr-4">
          <IAWordmark height={60} />
        </NavLink>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={linkClass}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <NavLink
            to="/book"
            data-testid={NAV.cta}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#C9A961] text-[#3B1F1A] text-[11px] uppercase tracking-[0.24em] hover:bg-[#3B1F1A] hover:text-[#C9A961] transition-colors"
          >
            Book a Consultation
            <ArrowUpRight size={14} />
          </NavLink>
        </div>

        <button
          className={`lg:hidden ${solid ? "text-[#3B1F1A]" : "text-[#FDFBF6]"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.menuToggle}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden bg-[#FDFBF6] border-t border-[#3B1F1A]/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`${l.testId}-mobile`}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.24em] ${
                      isActive ? "text-[#C9A961]" : "text-[#3B1F1A]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <NavLink
                to="/book"
                data-testid={`${NAV.cta}-mobile`}
                className="mt-2 inline-flex items-center justify-center h-11 rounded-full bg-[#C9A961] text-[#3B1F1A] text-sm tracking-wider"
              >
                Book a Consultation
              </NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
